import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import AssistService from "service/AssistService";
import { FieldVisitAssistVisitTypeResp } from "domain/payload/response/assist/FieldVisitAssistVisitTypeResp";
import { FieldVisitAssistListResp } from "domain/payload/response/assist/FieldVisitAssistListResp";
import { FieldVisitAssistDetailResp } from "domain/payload/response/assist/FieldVisitAssistDetailResp";
import DateRangeFilterDropdown, { DateRangeValue } from "component/DateRangeFilterDropdown";
import ProjectFilterDropdown from "component/ProjectFilterDropdown";
import { DashboardConfigProjectResp } from "domain/payload/response/dashboard/DashboardConfigProjectResp";
import FieldVisitDetailModal from "component/FieldVisitDetailModal";
import CustomDropdown, { DropdownOption } from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

const VISIT_TYPE_BADGE: Record<string, string> = {
  witness: "primary",
  backcheck: "warning",
  other: "secondary",
};

const FieldVisitAssist: React.FC = () => {
  const assistService = AssistService();
  const { profile } = useSelector((state: any) => state.auth);

  // --- filter state ---
  const [searchTerm, setSearchTerm] = useState("");
  const [projectIdTerm, setProjectIdTerm] = useState('');
  const [selectedVisitType, setSelectedVisitType] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  const FIELD_VISIT_OPTIONS: DropdownOption[] = [
    { id: "", name: 'All Visit Type' },
    { id: "witness", name: 'Witness' },
    { id: "backcheck", name: 'Backcheck' },
    { id: "other", name: 'Other' },
  ];

  // --- config state ---
  const [configLoading, setConfigLoading] = useState(false);
  const [projectList, setProjectList] = useState<DashboardConfigProjectResp[]>([]);
  const [visitType, setVisitTypeList] = useState<FieldVisitAssistVisitTypeResp[]>([]);

  // --- list state ---
  const [data, setData] = useState<FieldVisitAssistListResp[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(false);

  // --- detail state ---
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<FieldVisitAssistDetailResp | null>(null);

  // ----------------------------------------------------------------
  // Fetch config (project list + visit type list)
  // ----------------------------------------------------------------
  const fetchConfig = useCallback(async () => {
    if (!profile?.data?.token) return;
    try {
      setConfigLoading(true);
      const res = await assistService.assistFieldVisitConfig(profile.data.token);
      if (res.status === "T" && res.data) {
        setProjectList(res.data?.projectList ?? [])
        setVisitTypeList(res.data.visitType ?? []);
      }
    } catch (err) {
      console.error("Error fetching config:", err);
    } finally {
      setConfigLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ----------------------------------------------------------------
  // Fetch list
  // ----------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!profile?.data?.token) {
      window.alert("Token tidak ditemukan. Silakan login kembali.");
      return;
    }
    try {
      setLoading(true);
      const res = await assistService.assistFieldVisitList(
        profile.data.token,
        currentPage,
        pageSize,
        searchTerm || null,
        projectIdTerm || null,
        (selectedVisitType as "witness" | "backcheck" | "other") || null,
        dateFrom,
        dateTo
      );
      if (res.status === "T" && Array.isArray(res.data)) {
        setData(res.data);
        setTotalPages(res.pagination?.totalPage ?? 1);
        setTotalRecords(res.pagination?.totalRecords ?? res.data.length);
      } else {
        setData([]);
      }
    } catch (err: any) {
      console.error("Error fetching field visit list:", err);
      window.alert(`Error: ${err}`);
    } finally {
      setLoading(false);
      setIsSearchButtonDisabled(false);
    }
  }, [profile, currentPage, pageSize, searchTerm, projectIdTerm, selectedVisitType, dateFrom, dateTo]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------
  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setIsSearchButtonDisabled(true);
    fetchList();
  };

  const handleDateRangeChange = (value: DateRangeValue) => {
    setDateFrom(value.startDate);
    setDateTo(value.endDate);
    setCurrentPage(1);
  };

  const handleViewDetail = async (id: string) => {
    if (!profile?.data?.token) {
      window.alert("Token tidak ditemukan. Silakan login kembali.");
      return;
    }
    setShowDetailModal(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await assistService.assistFieldVisitDetail(profile.data.token, id);
      if (res.status === "T") {
        setDetailData(res.data ?? null);
      } else {
        throw new Error(res.message || "Gagal memuat detail");
      }
    } catch (err: any) {
      console.error("Error fetching detail:", err);
      window.alert(`Error: ${err}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setDetailData(null);
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex mb-3 align-items-center">
            <h5 style={{ flex: 1 }}>Field Visit Assist</h5>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4 bg-white rounded shadow-sm">

            {/* ── Filter Bar ── */}
            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">

              {/* Search */}
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 440 }}
                />
                <button type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSearchButtonDisabled}
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              {/* Date Range */}
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFilterDropdown onChange={handleDateRangeChange} />

                {/* Project */}
                <div className="col-12 col-md-auto" style={{ width: 240, maxHeight: 50 }}>
                  <ProjectFilterDropdown
                    projects={projectList}
                    defaultSelectedId={projectIdTerm === "" ? null : projectIdTerm}
                    allowAllProject={true}
                    onChange={(p) => {
                      setProjectIdTerm(p ? p.id : "");
                      setCurrentPage(1);
                    }}
                  />
                </div>

                {/* Jenis Kunjungan */}
                <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                  <CustomDropdown
                    value={selectedVisitType}
                    options={FIELD_VISIT_OPTIONS}
                    onChange={(opt) => {
                      setSelectedVisitType(opt.id ? String(opt.id) : "");
                      setCurrentPage(1);
                    }}
                    containerClassName="me-1"
                    align="right"
                    buttonStyle={{ width: "160" }}
                  />
                </div>
              </div>
            </div>

            {/* ── Table ── */}
            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>#</th>
                  <th>Project</th>
                  <th>User</th>
                  <th>Jenis Kunjungan</th>
                  <th>Subjek</th>
                  <th>Alasan</th>
                  <th>Tanggal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{item.projectName || "-"}</td>
                        <td>{item.userName || "-"}</td>
                        <td>
                          <span
                            className={`badge bg-${VISIT_TYPE_BADGE[item.visitType] ?? "secondary"
                              } text-capitalize`}
                          >
                            {item.visitType || "-"}
                          </span>
                        </td>
                        <td>{item.subjectName || "-"}</td>
                        <td>{item.reason || "-"}</td>
                        <td>{item.createdAt || "-"}</td>
                        <td>
                          <button
                            className="btn btn-outline-info btn-small-compact border-0"
                            onClick={() => handleViewDetail(item.id)}
                            title="Lihat Detail"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ── Pagination ── */}
            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {totalRecords === 0
                    ? "0 data"
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalRecords
                    )} of ${totalRecords}`}
                </span>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" style={{ border: 0 }} onClick={() => setCurrentPage(1)}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" style={{ border: 0 }} onClick={() => setCurrentPage(currentPage - 1)}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" style={{ border: 0 }} onClick={() => setCurrentPage(currentPage + 1)}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" style={{ border: 0 }} onClick={() => setCurrentPage(totalPages)}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      <FieldVisitDetailModal
        show={showDetailModal}
        loading={detailLoading}
        data={detailData}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default FieldVisitAssist;